import React from "react";
import { Card, Text, Group, ThemeIcon } from "@mantine/core";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ size?: number }>;
  color: string;
  layout?: "horizontal" | "vertical";
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function StatCard({ 
  title, 
  value, 
  icon: Icon, 
  color, 
  layout = "horizontal",
  size = "md",
  className = ""
}: StatCardProps) {
  const formattedValue =
    typeof value === "number" ? value.toLocaleString() : value;

  const sizeClasses = {
    sm: {
      card: "p-3",
      value: "text-base",
      title: "text-xs",
      icon: 20
    },
    md: {
      card: "p-4",
      value: "text-lg",
      title: "text-sm", 
      icon: 24
    },
    lg: {
      card: "p-6",
      value: "text-xl",
      title: "text-base",
      icon: 28
    }
  };

  const currentSize = sizeClasses[size];

  if (layout === "vertical") {
    return (
      <Card
        withBorder
        radius="md"
        className={`h-full transition-all duration-200 hover:shadow-lg text-center ${currentSize.card} ${className}`}
      >
        <div className="flex flex-col items-center justify-center h-full">
          <ThemeIcon
            variant="light"
            size="xl"
            color={color}
            className="mb-3"
          >
            <Icon size={currentSize.icon} />
          </ThemeIcon>
          <Text 
            size={currentSize.value as any} 
            fw={700} 
            className="text-gray-900 dark:text-white mb-1"
          >
            {formattedValue}
          </Text>
          <Text
            size={currentSize.title as any}
            className="text-gray-600 dark:text-gray-400"
          >
            {title}
          </Text>
        </div>
      </Card>
    );
  }

  // Horizontal layout (default)
  return (
    <Card
      withBorder
      radius="md"
      className={`h-full transition-all duration-200 hover:shadow-lg ${currentSize.card} ${className}`}
    >
      <Group justify="space-between" align="center" className="h-full">
        <div className="flex-1 min-w-0 h-full">
          <Text 
            size={currentSize.value as any} 
            fw={700} 
            className="text-gray-900 dark:text-white"
          >
            {formattedValue}
          </Text>
          <Text
            size={currentSize.title as any}
            className="text-gray-600 dark:text-gray-400"
            mt="xs"
          >
            {title}
          </Text>
        </div>
        <ThemeIcon
          variant="light"
          size="xl"
          color={color}
          className="flex-shrink-0 mt-1"
        >
          <Icon size={currentSize.icon} />
        </ThemeIcon>
      </Group>
    </Card>
  );
}
